import React from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { WidgetType } from 'types/dashboard.ui';
import DataSourceSelector from './data.sources';

interface WidgetSettingsProps {
  widgetType?: WidgetType;
  title: string;
  config: any;
  onTitleChange: (title: string) => void;
  onConfigChange: (config: any) => void;
}

/**
 * Компонент для настройки виджета (Шаг 2)
 */
const WidgetSettings: React.FC<WidgetSettingsProps> = ({
  widgetType,
  title,
  config,
  onTitleChange,
  onConfigChange,
}) => {
  const translate = useTranslate();

  const updateConfig = (updates: any) => {
    onConfigChange({ ...config, ...updates });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {translate('resources.dashboards.widget_wizard.widget_settings')}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {translate('resources.dashboards.widget_wizard.widget_settings_hint')}
      </Typography>

      {/* Общее поле - название виджета */}
      <TextField
        fullWidth
        label={translate('resources.dashboards.widget_wizard.widget_name')}
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        margin="normal"
        required
        helperText={translate('resources.dashboards.widget_wizard.widget_name_hint')}
      />

      {/* Специфичные настройки для Text виджета */}
      {widgetType === 'text' && (
        <TextField
          fullWidth
          label={translate('resources.dashboards.widget_wizard.content')}
          value={config.content || ''}
          onChange={(e) => updateConfig({ content: e.target.value })}
          margin="normal"
          multiline
          rows={4}
          helperText={translate('resources.dashboards.widget_wizard.content_hint')}
        />
      )}

      {/* Специфичные настройки для Chart виджета */}
      {widgetType === 'chart' && (
        <>
          <Box sx={{ mt: 3, mb: 2 }}>
            <DataSourceSelector
              value={config.dataSource?.items || []}
              onChange={(items) => updateConfig({ dataSource: { items } })}
              multiple={true}
              label={translate('resources.dashboards.widget_wizard.data_source')}
            />
          </Box>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>{translate('resources.dashboards.widget_wizard.chart_type')}</InputLabel>
            <Select
              value={config.chartType || 'line'}
              onChange={(e) => updateConfig({ chartType: e.target.value })}
              label={translate('resources.dashboards.widget_wizard.chart_type')}
            >
              <MenuItem value="line">
                {translate('resources.dashboards.widget_wizard.chart_line')}
              </MenuItem>
              <MenuItem value="bar">
                {translate('resources.dashboards.widget_wizard.chart_bar')}
              </MenuItem>
              <MenuItem value="area">
                {translate('resources.dashboards.widget_wizard.chart_area')}
              </MenuItem>
              <MenuItem value="pie">
                {translate('resources.dashboards.widget_wizard.chart_pie')}
              </MenuItem>
            </Select>
          </FormControl>
        </>
      )}

      {/* Специфичные настройки для Table виджета */}
      {widgetType === 'table' && (
        <Box sx={{ mt: 3 }}>
          <DataSourceSelector
            value={config.dataSource?.items || []}
            onChange={(items) => updateConfig({ dataSource: { items } })}
            multiple={true}
            label={translate('resources.dashboards.widget_wizard.data_source')}
          />
        </Box>
      )}

      {/* Специфичные настройки для Gauge виджета */}
      {widgetType === 'gauge' && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {translate('resources.dashboards.widget_wizard.gauge_single_param_hint')}
          </Typography>
          
          <DataSourceSelector
            value={config.dataSource && config.dataSource.paramId ? [config.dataSource] : []}
            onChange={(items) => {
              // Для gauge всегда берем только первый элемент
              if (items.length > 0) {
                updateConfig({ dataSource: items[0] });
              } else {
                updateConfig({ dataSource: { deviceId: '', archiveTypeId: '', paramId: '' } });
              }
            }}
            multiple={false}
            label={translate('resources.dashboards.widget_wizard.data_source')}
            maxItems={1}
          />
          
          {/* Диапазон значений */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              type="number"
              label={translate('resources.dashboards.widget_wizard.min_value')}
              value={config.minValue ?? 0}
              onChange={(e) => updateConfig({ minValue: parseFloat(e.target.value) || 0 })}
            />
            <TextField
              fullWidth
              type="number"
              label={translate('resources.dashboards.widget_wizard.max_value')}
              value={config.maxValue ?? 100}
              onChange={(e) => updateConfig({ maxValue: parseFloat(e.target.value) || 100 })}
            />
          </Box>
        </Box>
      )}

      {/* Специфичные настройки для Schema виджета */}
      {widgetType === 'schema' && (
        <Box sx={{ mt: 2 }}>
          <DataSourceSelector
            label={translate('resources.dashboards.widgets.schema.data_sources')}
            value={config.dataSource?.items || []}
            onChange={(newItems) => updateConfig({ 
              dataSource: { items: newItems } 
            })}
          />
        </Box>
      )}

      {/* Специфичные настройки для Map виджета */}
      {widgetType === 'map' && (
        <Box sx={{ mt: 2 }}>
          <DataSourceSelector
            label={translate('resources.dashboards.widgets.map.data_sources')}
            value={config.dataSource?.items || []}
            onChange={(newItems) => updateConfig({ 
              dataSource: { items: newItems } 
            })}
          />
        </Box>
      )}
    </Box>
  );
};

export default WidgetSettings;
